import * as Portal from "@radix-ui/react-portal";
import * as Sentry from "@sentry/react";
import { flexRender } from "@tanstack/react-table";
import * as React from "react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ValidationRoot } from "@/components/shared/form/shared/form-validation";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cleanVatPreSave } from "@/lib/utils";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";

import { lineItemValidationSchema } from "../../../validations/line-item";
import {
  UpdateInvoiceFields,
  useInvoiceReviewContext,
  useInvoiceReviewStateDispatch,
  useSaveInvoice,
} from "../../ReviewProvider";
import { InvoiceModes } from "../SplitInvoiceForm";
import { CanvasBulkEditActionBar } from "./BulkEditActionBar";
import { TotalAmountsOverview } from "./InvoiceTotalsOverview";
import { AddLineItemBar } from "./add-line-item-action-bar";
import { BulkEditLineItemActionBar } from "./bulk-edit-line-item-action-bar";
import { useLineItemTableInstance } from "./table-instance-provider";
import { cleanNewAmountValueCanvas } from "./utils";

type Props = {
  invoiceMode: InvoiceModes;
};

export function LineItemTable<TData, TValue>({ invoiceMode }: Props) {
  const { table, rowSelection } = useLineItemTableInstance();
  const {
    invoiceReviewState,
    selectedBoundingBoxIndexes,
    setSelectedBoundingBoxIndexes,
    refetch,
    updateEditedBoundingBoxIndexes,
  } = useInvoiceReviewContext();
  const {
    invoiceData,
    accounts,
    taxCodes,
    boundingBoxes,
    editedBoundingBoxIndexes,
  } = invoiceReviewState;
  const dispatch = useInvoiceReviewStateDispatch();
  const { saveInvoice } = useSaveInvoice();
  const [selectionType, setSelectionType] = useState<"row" | "item" | null>(
    null,
  );
  const { costCenters } = useGetAllCostCenters();

  React.useEffect(() => {
    if (table.getSelectedRowModel().rows.length > 0) {
      setSelectionType("row");
    } else {
      if (selectedBoundingBoxIndexes.size > 0) {
        setSelectionType("item");
      } else {
        setSelectionType(null);
      }
    }
  }, [rowSelection]);

  const [boundingBoxIndexToLineItemIdMap, setBoundingBoxIndexToLineItemIdMap] =
    useState<Map<number, string>>(new Map());

  const [filteredLineItemIds, setFilteredLineItemIds] = useState<string[]>([]);
  const filteredLineItemIdsRef = useRef(filteredLineItemIds);
  const editedBoundingBoxIndexesRef = useRef(editedBoundingBoxIndexes);
  const amountBoxesRef = useRef(boundingBoxes);
  const selectedAmountBoxesRef = useRef(selectedBoundingBoxIndexes);

  React.useEffect(() => {
    const ids = invoiceData?.lineItems
      ?.filter((lineItem) => {
        if (typeof lineItem?.index === "undefined" || lineItem.index === null)
          return false;
        return selectedBoundingBoxIndexes.has(lineItem.index);
      })
      .map((lineItem) => lineItem.lineItemId)
      .filter((id): id is string => id !== undefined);
    setFilteredLineItemIds(ids ?? []);
  }, [invoiceData, selectedBoundingBoxIndexes]);

  React.useEffect(() => {
    filteredLineItemIdsRef.current = filteredLineItemIds;
  }, [filteredLineItemIds]);

  React.useEffect(() => {
    editedBoundingBoxIndexesRef.current = editedBoundingBoxIndexes;
  }, [editedBoundingBoxIndexes]);

  React.useEffect(() => {
    selectedAmountBoxesRef.current = selectedBoundingBoxIndexes;
    if (selectedBoundingBoxIndexes && selectedBoundingBoxIndexes?.size > 0) {
      setSelectionType("item");
    } else {
      if (table.getSelectedRowModel().rows.length > 0) {
        setSelectionType("row");
      } else {
        setSelectionType(null);
      }
    }
  }, [selectedBoundingBoxIndexes]);

  React.useEffect(() => {
    amountBoxesRef.current = boundingBoxes;
  }, [boundingBoxes]);

  const tableRef = React.useRef(null);

  // **************** Change Handlers ****************

  const handleDeleteSelected = async () => {
    setSelectionType(null);

    const lineItemIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.lineItem.lineItemId);

    await saveInvoice(
      UpdateInvoiceFields.DeleteLineItem,
      undefined,
      lineItemIds,
    );

    if (editedBoundingBoxIndexes && editedBoundingBoxIndexes.length > 0) {
      const entriesArray = Array.from(
        boundingBoxIndexToLineItemIdMap.entries(),
      );

      const neweditedBoundingBoxIndexes = editedBoundingBoxIndexes.filter(
        (index) => {
          const entry = entriesArray.find(([idx, id]) => idx === index);
          if (entry && lineItemIds.includes(entry[1])) {
            return false;
          }
          return true;
        },
      );
      updateEditedBoundingBoxIndexes(neweditedBoundingBoxIndexes);
    }
  };

  const handleDeleteSelectedCanvas = async () => {
    const selectedIndexes = Array.from(selectedBoundingBoxIndexes);
    const lineItemIds = selectedIndexes
      .map((index) => boundingBoxIndexToLineItemIdMap.get(index))
      .filter((id): id is string => id !== undefined);

    if (lineItemIds && lineItemIds?.length > 0) {
      await saveInvoice(
        UpdateInvoiceFields.DeleteLineItem,
        undefined,
        lineItemIds,
      );

      if (editedBoundingBoxIndexes && editedBoundingBoxIndexes.length > 0) {
        const neweditedBoundingBoxIndexes = editedBoundingBoxIndexes.filter(
          (index) => !selectedIndexes.includes(index),
        );
        updateEditedBoundingBoxIndexes(neweditedBoundingBoxIndexes);
      }

      setSelectionType(null);
    }
  };

  const handleCombinedAccountChange = (value: any) => {
    const rows = table.getSelectedRowModel().rows;
    const ids = rows.map((row) => row.original.lineItemId);

    const selectedAccount = accounts?.find(
      (account) => account.accountId === value,
    );

    const updatedFields = {
      accountId: value,
    };

    dispatch({
      type: UpdateInvoiceFields.ExpenseAccount,
      payload: selectedAccount,
      ids,
    });
    saveInvoice(UpdateInvoiceFields.ExpenseAccount, updatedFields, ids);
  };

  const handleCombinedCostCentersChange = (value: any) => {
    const rows = table.getSelectedRowModel().rows;
    const ids = rows.map((row) => row.original.lineItemId);

    const updatedFields = {
      costCenterId: value !== "unknown" ? value : null,
    };

    dispatch({
      type: UpdateInvoiceFields.CostCenterId,
      payload: value,
      ids,
    });
    saveInvoice(UpdateInvoiceFields.CostCenterId, updatedFields, ids);
  };

  const createMissingLineItems = async (options: {
    name?: string;
    accountId?: string;
    taxCodeId?: string;
    vatRate?: number;
  }) => {
    const { name, accountId, taxCodeId, vatRate } = options;

    for (let i = 0; i < selectedAmountBoxesRef.current.size; i++) {
      const selectedAmountBoxes = Array.from(selectedAmountBoxesRef.current);
      const idx = selectedAmountBoxes[i];

      const isProcessed = (editedBoundingBoxIndexesRef.current ?? []).includes(
        idx,
      );

      if (amountBoxesRef.current && !isProcessed) {
        const polygon = amountBoxesRef.current.find(
          (value) => value.index === idx,
        )?.coordinates;

        const cleanAmount = cleanNewAmountValueCanvas(polygon?.content);

        const newLineItem: any = {
          name,
          accountId,
          taxCodeId,
          vatRate,
          index: idx,
        };

        if (invoiceMode === InvoiceModes.Netto) {
          newLineItem.nettoAmount = cleanAmount;
        } else {
          newLineItem.amount = cleanAmount;
        }

        if (vatRate !== undefined) {
          newLineItem.vatRate = vatRate;
        } else {
          const selectedTaxCode = taxCodes?.find(
            (taxCode) => taxCode.taxCodeId === taxCodeId,
          );
          const newVatRate = Number(selectedTaxCode?.vatRate);
          if (!Number.isNaN(newVatRate)) {
            newLineItem.vatRate = newVatRate;
          }
        }
        saveInvoice(UpdateInvoiceFields.CreateLineItem, newLineItem).then(
          (createdLineItemId) => {
            if (createdLineItemId) {
              setBoundingBoxIndexToLineItemIdMap((prevMap) =>
                new Map(prevMap).set(idx, createdLineItemId),
              );
            }
          },
        );
      }
    }
  };

  const handleBulkSave = async (values: any) => {
    const { name, accountId, taxCodeId } = values;
    const ids = filteredLineItemIdsRef.current;

    try {
      const updatededitedBoundingBoxIndexes = [
        ...(editedBoundingBoxIndexes ?? []),
        ...Array.from(selectedAmountBoxesRef.current ?? new Set()),
      ];

      await createMissingLineItems({ name, accountId, taxCodeId });

      //refetch after creating new line items
      if (!ids.length) {
        await refetch();
      }
      if (ids.length) {
        if (name) {
          await saveInvoice(UpdateInvoiceFields.Name, { name }, ids);
        }
        if (taxCodeId) {
          await handleCombinedTaxCodeChange(taxCodeId, ids);
        }
        if (accountId) {
          await saveInvoice(
            UpdateInvoiceFields.ExpenseAccount,
            { accountId },
            ids,
          );
        }
      }

      updateEditedBoundingBoxIndexes(updatededitedBoundingBoxIndexes);
    } catch (error) {
      Sentry.captureException(error);
      await refetch();
    }
  };

  const handleCombinedNameChange = async (value: string) => {
    const ids = filteredLineItemIdsRef.current;

    const updatedFields = {
      name: value,
    };

    dispatch({
      type: UpdateInvoiceFields.Name,
      payload: value,
      ids,
    });

    const oldeditedBoundingBoxIndexes = editedBoundingBoxIndexes;

    try {
      const updatededitedBoundingBoxIndexes = [
        ...(editedBoundingBoxIndexes ?? []),
        ...Array.from(selectedAmountBoxesRef.current ?? new Set()),
      ];

      updateEditedBoundingBoxIndexes(updatededitedBoundingBoxIndexes);

      await saveInvoice(UpdateInvoiceFields.Name, updatedFields, ids);
      await createMissingLineItems({ name: value });
    } catch (error) {
      Sentry.captureException(error);
      updateEditedBoundingBoxIndexes(oldeditedBoundingBoxIndexes ?? []);
      refetch();
    }
  };

  const handleCombinedAccountChangeCanvas = async (value: any) => {
    const ids = filteredLineItemIdsRef.current;
    const selectedAccount = accounts?.find(
      (account) => account.accountId === value,
    );

    const updatedFields = {
      accountId: value,
    };

    dispatch({
      type: UpdateInvoiceFields.ExpenseAccount,
      payload: selectedAccount,
      ids,
    });

    const oldeditedBoundingBoxIndexes = editedBoundingBoxIndexes;

    try {
      const updatededitedBoundingBoxIndexes = [
        ...(editedBoundingBoxIndexes ?? []),
        ...Array.from(selectedAmountBoxesRef.current ?? new Set()),
      ];

      updateEditedBoundingBoxIndexes(updatededitedBoundingBoxIndexes);

      await saveInvoice(UpdateInvoiceFields.ExpenseAccount, updatedFields, ids);
      await createMissingLineItems({ accountId: selectedAccount?.accountId });
    } catch (error) {
      Sentry.captureException(error);
      updateEditedBoundingBoxIndexes(oldeditedBoundingBoxIndexes ?? []);
      refetch();
    }
  };

  const handleCombinedTaxCodeChange = async (
    value: any,
    lineItemIds?: string[],
  ) => {
    const ids =
      lineItemIds ??
      table.getSelectedRowModel().rows.map((row) => row.original.lineItemId);

    const selectedTaxCode = taxCodes?.find(
      (taxCode) => taxCode.taxCodeId === value,
    );

    const cleanedVatRate = cleanVatPreSave(selectedTaxCode?.vatRate);

    dispatch({
      type: UpdateInvoiceFields.VatRate,
      payload: cleanedVatRate,
      ids,
    });

    const updatedFields = {
      taxCodeId: selectedTaxCode?.taxCodeId,
      vatRate: cleanedVatRate,
    };

    await saveInvoice(
      UpdateInvoiceFields.VatRateAndTaxCodeId,
      updatedFields,
      ids,
    );
  };

  //Note: Not being triggered currently (replaced by handleBulkSave), but we will need to revisit the updating logic soon to fix the duplicates problem, so leaving here for now.
  const handleCombinedTaxCodeChangeCanvas = async (value: any) => {
    const ids = filteredLineItemIdsRef.current;

    const selectedTaxCode = taxCodes?.find(
      (taxCode) => taxCode.taxCodeId === value,
    );

    const updatedFields = {
      taxCodeId: value,
    };

    const updatededitedBoundingBoxIndexes = [
      ...(editedBoundingBoxIndexes ?? []),
      ...Array.from(selectedAmountBoxesRef.current ?? new Set()),
    ];

    await updateEditedBoundingBoxIndexes(updatededitedBoundingBoxIndexes);

    dispatch({
      type: UpdateInvoiceFields.TaxCode,
      payload: selectedTaxCode,
      ids,
    });

    const oldeditedBoundingBoxIndexes = editedBoundingBoxIndexes;
    try {
      await saveInvoice(UpdateInvoiceFields.TaxCodeId, updatedFields, ids);

      const vatRate = Number.parseFloat(selectedTaxCode?.vatRate as any);
      if (vatRate !== null && !isNaN(vatRate)) {
        await dispatch({
          type: UpdateInvoiceFields.VatRate,
          payload: vatRate,
          ids,
        });

        //dirty temporary hack to avoid problem with local tax code state being wrongly overwritten - if I don't fix this by June 27 I owe @maco and @markus dinner
        setTimeout(async () => {
          await saveInvoice(
            UpdateInvoiceFields.VatRate,
            { vatRate: vatRate },
            ids,
          );
        }, 5000);

        await createMissingLineItems({
          taxCodeId: selectedTaxCode?.taxCodeId,
          vatRate: vatRate,
        });
      } else {
        await createMissingLineItems({ taxCodeId: selectedTaxCode?.taxCodeId });
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      updateEditedBoundingBoxIndexes(oldeditedBoundingBoxIndexes ?? []);
      refetch();
    }
  };

  const handleDeselectAll = () => {
    setSelectedBoundingBoxIndexes(new Set());
  };

  return (
    <div className="h-full gap-2 flex flex-col">
      <Table ref={tableRef}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: `${header.getSize()}px`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingLeft: header.id === "select" ? "5px" : "0px",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                <ValidationRoot
                  schema={lineItemValidationSchema}
                  values={row.original}
                >
                  {row.getVisibleCells().map((cell) => {
                    const hasMeta =
                      cell.getContext().cell.column.columnDef.meta;
                    const style =
                      hasMeta?.getCellContext?.(cell.getContext())?.style ?? {};

                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          paddingRight: "0px",
                          paddingTop: "0px",
                          paddingBottom: "0px",
                          paddingLeft: cell.id.includes("select")
                            ? "5px"
                            : "0px",
                          ...style,
                        }}
                        className="truncate-short"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </ValidationRoot>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                Keine Buchungen
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Portal.Root container={document.getElementById("line-item-bar-portal")}>
        <div className="flex flex-col gap-2 px-5 my-2">
          {selectionType === "row" ? (
            <BulkEditLineItemActionBar
              table={table}
              handleCombinedAccountChange={handleCombinedAccountChange}
              accounts={accounts}
              costCenters={costCenters}
              handleCombinedTaxCodeChange={handleCombinedTaxCodeChange}
              handleCombinedCostCentersChange={handleCombinedCostCentersChange}
              taxCodes={taxCodes}
              handleDeleteSelected={handleDeleteSelected}
            />
          ) : (
            <AddLineItemBar
              accounts={accounts}
              rowCount={table.getRowCount()}
              taxCodes={taxCodes}
              invoiceMode={invoiceMode}
            />
          )}
          <TotalAmountsOverview
            invoiceMode={invoiceMode}
            totalAmount={invoiceData?.totalAmount ?? 0}
            lineItems={invoiceData?.lineItems ?? []}
            isNettoInvoice={invoiceData?.isNettoInvoice ?? false}
            currency={invoiceData?.currency ?? ""}
          />
        </div>
      </Portal.Root>
      {createPortal(
        <div className="flex justify-center">
          {selectionType === "item" ? (
            <CanvasBulkEditActionBar
              table={table}
              handleBulkSave={handleBulkSave}
              handleCombinedNameChange={handleCombinedNameChange}
              handleCombinedAccountChange={handleCombinedAccountChangeCanvas}
              accounts={accounts}
              handleCombinedTaxCodeChange={handleCombinedTaxCodeChangeCanvas}
              taxCodes={taxCodes}
              handleDeleteSelected={handleDeleteSelectedCanvas}
              handleDeselectAll={handleDeselectAll}
              selectedItems={selectedBoundingBoxIndexes}
              totalAmountBoxes={boundingBoxes ?? []}
            />
          ) : null}
        </div>,
        // @ts-ignore - TS is not smart here
        document.getElementById("invoice-review-screen"),
      )}
    </div>
  );
}
